/**
 * Report definitions for each module.
 * Each module exports a reports array with { id, label, desc, icon, filters?, columns?, drillDown?, lineItemColumns? }
 * drillDown: { keyField: 'field', route: '/path' } — row click navigates to record detail
 * lineItemColumns: [...] — columns for flattened line-item sub-rows
 */
import {
  Layers, TrendingDown, DollarSign, UserCheck, Trash2,
  Receipt, Clock, Building2, FileCheck, AlertTriangle,
  Package, Truck, ClipboardList,
  FileText, BarChart3, Award, PiggyBank,
  FileEdit, Calendar, Users,
  Briefcase, Shield,
  Hash, Grid3X3,
  Database, Tag, FolderTree,
  SlidersHorizontal,
} from 'lucide-react';

const MGMT = { color: 'bg-slate-900', iconColor: 'text-white', badge: 'Advanced', badgeColor: 'bg-slate-700 text-white' };

// ═══════════════════════ ASSET REPORTS ═══════════════════════

export const ASSET_REPORTS = [
  {
    id: 'management', label: 'General Management Report', icon: SlidersHorizontal, ...MGMT, advancedModule: 'assets',
    desc: 'Query any asset attribute with full boolean logic. Save and recall filters.',
  },
  {
    id: 'register', label: 'Asset Register', icon: Layers,
    desc: 'Complete listing of all assets with status, location, value, and depreciation method.',
    color: 'bg-indigo-50', iconColor: 'text-indigo-600', badge: 'Core',
    drillDown: { keyField: 'asset_number', route: '/assets', param: 'edit' },
    filters: [
      { key: 'class_code', label: 'Class', type: 'text', placeholder: 'e.g. IT-EQUIP' },
      { key: 'status', label: 'Status', type: 'select', options: [
        { value: 'active', label: 'Active' }, { value: 'assigned', label: 'Assigned' },
        { value: 'in_storage', label: 'In Storage' }, { value: 'in_maintenance', label: 'In Maintenance' },
        { value: 'disposed', label: 'Disposed' }, { value: 'written_off', label: 'Written Off' },
      ]},
      { key: 'asset_type', label: 'Type', type: 'select', options: [
        { value: 'fixed_asset', label: 'Fixed Asset' }, { value: 'current_asset', label: 'Current Asset' },
      ]},
      { key: 'site_code', label: 'Site', type: 'text' },
    ],
    columns: [
      { key: 'asset_number', label: 'Asset #', mono: true },
      { key: 'description', label: 'Description' },
      { key: 'class_name', label: 'Class' },
      { key: 'status', label: 'Status' },
      { key: 'site_name', label: 'Site' },
      { key: 'depr_method', label: 'Depr' },
      { key: 'acquisition_cost', label: 'Acq. Cost', align: 'right' },
      { key: 'accumulated_depreciation', label: 'Accum. Depr', align: 'right' },
      { key: 'net_book_value', label: 'NBV', align: 'right' },
    ],
  },
  {
    id: 'depreciation-schedule', label: 'Depreciation Schedule', icon: TrendingDown,
    desc: 'Period-by-period depreciation entries with method indicator (SL/RB).',
    color: 'bg-emerald-50', iconColor: 'text-emerald-600', badge: 'Finance',
    filters: [
      { key: 'asset_number', label: 'Asset #', type: 'text', placeholder: 'e.g. AST-000001' },
      { key: 'class_code', label: 'Class Code', type: 'text' },
    ],
    columns: [
      { key: 'asset_number', label: 'Asset #', mono: true },
      { key: 'depr_method', label: 'Method' },
      { key: 'period', label: 'Period', align: 'right' },
      { key: 'period_date', label: 'Period Date' },
      { key: 'depreciation_amount', label: 'Depr Amount', align: 'right' },
      { key: 'accumulated_depreciation', label: 'Accum. Depr', align: 'right' },
      { key: 'net_book_value', label: 'NBV', align: 'right' },
    ],
  },
  {
    id: 'valuation', label: 'Asset Valuation Summary', icon: DollarSign,
    desc: 'Acquisition cost, accumulated depreciation, and NBV summarised by class.',
    color: 'bg-emerald-50', iconColor: 'text-emerald-600', badge: 'Finance', badgeColor: 'bg-emerald-100 text-emerald-700',
  },
  {
    id: 'assignments', label: 'Assignment History', icon: UserCheck,
    desc: 'Full history of asset assignments, transfers, and returns.',
    color: 'bg-blue-50', iconColor: 'text-blue-600',
    filters: [
      { key: 'asset_number', label: 'Asset #', type: 'text' },
      { key: 'action', label: 'Action', type: 'select', options: [
        { value: 'assign', label: 'Assign' }, { value: 'transfer', label: 'Transfer' }, { value: 'return', label: 'Return' },
      ]},
    ],
  },
  {
    id: 'disposals', label: 'Disposal Report', icon: Trash2,
    desc: 'All disposed and written-off assets with depreciation method, proceeds and reasons.',
    color: 'bg-red-50', iconColor: 'text-red-600',
    columns: [
      { key: 'asset_number', label: 'Asset #', mono: true },
      { key: 'description', label: 'Description' },
      { key: 'class_name', label: 'Class' },
      { key: 'status', label: 'Status' },
      { key: 'depr_method', label: 'Depr' },
      { key: 'acquisition_cost', label: 'Acq. Cost', align: 'right' },
      { key: 'net_book_value', label: 'NBV', align: 'right' },
      { key: 'disposal_proceeds', label: 'Proceeds', align: 'right' },
      { key: 'disposal_date', label: 'Disposed' },
      { key: 'disposal_reason', label: 'Reason' },
    ],
  },
];

// ═══════════════════════ INVOICE REPORTS ═══════════════════════

export const INVOICE_REPORTS = [
  {
    id: 'management', label: 'General Management Report', icon: SlidersHorizontal, ...MGMT, advancedModule: 'invoices',
    desc: 'Query any invoice attribute with full boolean logic. Save and recall filters.',
    drillDown: { keyField: 'invoice_ref', route: '/invoices' },
    reportFormats: [
      {
        value: 'header_only',
        label: 'Header Details Only',
        type: 'flat',
        linesField: 'line_items',
        columns: [
          { key: 'invoice_ref', label: 'Invoice Ref', mono: true },
          { key: 'status', label: 'Status' },
          { key: 'invoice_date', label: 'Invoice Date' },
          { key: 'due_date', label: 'Due Date' },
          { key: 'supplier_invoice_number', label: 'Supplier Inv #' },
          { key: 'supplier_name', label: 'Supplier Name' },
          { key: 'order_number', label: 'Order Number' },
          { key: '_line_count', label: '# Line Items', align: 'right' },
          { key: '_doc_value', label: 'Reported Invoice Value', align: 'right', currency: true },
        ],
        valueFields: ['invoice_net_total', 'invoice_gross_total'],
        totalField: '_doc_value',
        totalLabel: 'Total Invoice Value',
        excludeStatuses: ['REJECTED', 'CANCELLED'],
        noteText: 'Any REJECTED or CANCELLED invoice listed will not be part of the total invoice value.',
      },
      {
        value: 'line_only',
        label: 'Line Details Only',
        type: 'flat',
        linesField: 'line_items',
        parentFields: ['invoice_ref', 'status'],
        columns: [
          { key: 'invoice_ref', label: 'Invoice Ref', mono: true },
          { key: 'status', label: 'Status' },
          { key: 'item_num', label: 'Item No', align: 'right' },
          { key: 'item_description', label: 'Description' },
          { key: 'item_code', label: 'Item Code' },
          { key: 'unit', label: 'Unit' },
          { key: 'invoice_quantity', label: 'Inv Qty', align: 'right', qty: true },
          { key: 'invoice_unit_price', label: 'Inv Unit Price', align: 'right', currency: true },
          { key: 'invoice_net_price', label: 'Inv Net Price', align: 'right', currency: true },
          { key: 'match_status', label: 'Match Status' },
        ],
        totalField: 'invoice_net_price',
        totalLabel: 'Report Total',
        excludeStatuses: ['REJECTED', 'CANCELLED'],
        noteText: 'Any REJECTED or CANCELLED invoice listed will not be part of the report total.',
      },
      {
        value: 'combo',
        label: 'Header & Line Details',
        type: 'combo',
        linesField: 'line_items',
        primaryKeyField: 'invoice_ref',
        primaryKeyLabel: 'Invoice Ref',
        valueFields: ['invoice_net_total', 'invoice_gross_total'],
        headerSections: [
          [
            { key: 'status', label: 'Status' },
            { key: 'invoice_date', label: 'Invoice Date' },
            { key: 'due_date', label: 'Due Date' },
            { key: 'match_status', label: 'Match Status' },
          ],
          [
            { key: 'supplier_invoice_number', label: 'Supplier Inv #' },
            { key: 'supplier_name', label: 'Supplier' },
            { key: 'supplier_code', label: 'Supplier Code' },
            { key: 'order_number', label: 'Order Number' },
          ],
          [
            { key: 'currency', label: 'Currency' },
            { key: 'created_by', label: 'Created By' },
            { key: 'created_at', label: 'Created Date' },
          ],
        ],
        lineColumns: [
          { key: 'item_num', label: 'Item', align: 'right' },
          { key: 'item_description', label: 'Description' },
          { key: 'unit', label: 'Unit' },
          { key: 'invoice_quantity', label: 'Inv Qty', align: 'right', qty: true },
          { key: 'invoice_unit_price', label: 'Inv Unit Price', align: 'right', currency: true },
          { key: 'invoice_net_price', label: 'Inv Net Price', align: 'right', currency: true },
          { key: 'po_quantity', label: 'PO Qty', align: 'right', qty: true },
          { key: 'po_unit_price', label: 'PO Unit Price', align: 'right', currency: true },
          { key: 'po_net_price', label: 'PO Net Price', align: 'right', currency: true },
          { key: 'match_status', label: 'Match Status' },
        ],
      },
    ],
  },
  {
    id: 'register', label: 'Invoice Register', icon: Receipt,
    desc: 'Complete invoice listing with status, supplier, and value.',
    color: 'bg-indigo-50', iconColor: 'text-indigo-600', badge: 'Core',
    drillDown: { keyField: 'invoice_ref', route: '/invoices' },
    lineItemColumns: [
      { key: 'li_item_code', label: 'Item Code', mono: true },
      { key: 'li_item_description', label: 'Description' },
      { key: 'li_quantity', label: 'Qty', align: 'right' },
      { key: 'li_unit_price', label: 'Unit Price', align: 'right' },
      { key: 'li_net_price', label: 'Net Price', align: 'right' },
    ],
    filters: [
      { key: 'invoice_ref', label: 'Invoice Ref', type: 'text', placeholder: 'e.g. INV-001' },
      { key: 'status', label: 'Status', type: 'select', options: [
        { value: 'DRAFT', label: 'Draft' }, { value: 'MATCHED', label: 'Matched' },
        { value: 'PENDING_APPROVAL', label: 'Pending Approval' }, { value: 'APPROVED', label: 'Approved' },
        { value: 'PAID', label: 'Paid' }, { value: 'REJECTED', label: 'Rejected' },
      ]},
      { key: 'supplier_name', label: 'Supplier', type: 'text' },
      { key: 'created_by', label: 'Created By', type: 'text' },
      { key: 'date_from', label: 'From', type: 'date' },
      { key: 'date_to', label: 'To', type: 'date' },
    ],
  },
  {
    id: 'aging', label: 'Invoice Aging', icon: Clock,
    desc: 'Unpaid invoices aged by current, 30, 60, and 90+ day buckets.',
    color: 'bg-amber-50', iconColor: 'text-amber-600', badge: 'Finance', badgeColor: 'bg-amber-100 text-amber-700',
  },
  {
    id: 'supplier-summary', label: 'Supplier Invoice Summary', icon: Building2,
    desc: 'Invoice totals and outstanding balances grouped by supplier.',
    color: 'bg-blue-50', iconColor: 'text-blue-600',
  },
  {
    id: 'match-variance', label: 'Match Variance Report', icon: AlertTriangle,
    desc: 'Invoices with matching variances or unmatched status.',
    color: 'bg-red-50', iconColor: 'text-red-600',
  },
  {
    id: 'payment-readiness', label: 'Payment Readiness', icon: FileCheck,
    desc: 'Invoices approved and ready for payment processing.',
    color: 'bg-emerald-50', iconColor: 'text-emerald-600',
  },
];

// ═══════════════════════ GRN REPORTS ═══════════════════════

export const GRN_REPORTS = [
  {
    id: 'management', label: 'General Management Report', icon: SlidersHorizontal, ...MGMT, advancedModule: 'grn',
    desc: 'Query any GRN attribute with full boolean logic. Save and recall filters.',
    drillDown: { keyField: 'grn_number', route: '/goods-received', param: 'edit' },
    reportFormats: [
      {
        value: 'header_only',
        label: 'Header Details Only',
        type: 'flat',
        linesField: 'lines',
        columns: [
          { key: 'grn_number', label: 'GRN Number', mono: true },
          { key: 'order_number', label: 'Order Number' },
          { key: 'received_date', label: 'Received Date' },
          { key: 'delivery_note_ref', label: 'Delivery Note Ref' },
          { key: 'received_by', label: 'Received By' },
          { key: '_line_count', label: '# Lines', align: 'right' },
        ],
      },
      {
        value: 'line_only',
        label: 'Line Details Only',
        type: 'flat',
        linesField: 'lines',
        parentFields: ['grn_number', 'order_number'],
        columns: [
          { key: 'grn_number', label: 'GRN Number', mono: true },
          { key: 'order_number', label: 'Order Number' },
          { key: 'item_num', label: 'Item No', align: 'right' },
          { key: 'item_description', label: 'Description' },
          { key: 'item_code', label: 'Item Code' },
          { key: 'unit', label: 'Unit' },
          { key: 'qty_ordered', label: 'Qty Ordered', align: 'right', qty: true },
          { key: 'qty_received', label: 'Qty Received', align: 'right', qty: true },
          { key: 'qty_rejected', label: 'Qty Rejected', align: 'right', qty: true },
          { key: 'reject_reason', label: 'Reject Reason' },
          { key: 'reject_action', label: 'Reject Action' },
        ],
      },
      {
        value: 'combo',
        label: 'Header & Line Details',
        type: 'combo',
        linesField: 'lines',
        primaryKeyField: 'grn_number',
        primaryKeyLabel: 'GRN Number',
        headerSections: [
          [
            { key: 'order_number', label: 'Order Number' },
            { key: 'received_date', label: 'Received Date' },
          ],
          [
            { key: 'delivery_note_ref', label: 'Delivery Note Ref' },
            { key: 'received_by', label: 'Received By' },
          ],
          [
            { key: 'notes', label: 'Notes' },
            { key: 'created_at', label: 'Created Date' },
          ],
        ],
        lineColumns: [
          { key: 'item_num', label: 'Item', align: 'right' },
          { key: 'item_description', label: 'Description' },
          { key: 'item_code', label: 'Item Code' },
          { key: 'unit', label: 'Unit' },
          { key: 'qty_ordered', label: 'Qty Ordered', align: 'right', qty: true },
          { key: 'qty_received', label: 'Qty Received', align: 'right', qty: true },
          { key: 'qty_rejected', label: 'Qty Rejected', align: 'right', qty: true },
          { key: 'reject_reason', label: 'Reject Reason' },
          { key: 'reject_action', label: 'Reject Action' },
        ],
      },
    ],
  },
  {
    id: 'register', label: 'GRN Register', icon: Package,
    desc: 'All goods receipt notes with supplier, order, and value details.',
    color: 'bg-indigo-50', iconColor: 'text-indigo-600', badge: 'Core',
    drillDown: { keyField: 'grn_number', route: '/goods-received', param: 'edit' },
    filters: [
      { key: 'grn_number', label: 'GRN Number', type: 'text', placeholder: 'e.g. GRN-001' },
      { key: 'supplier_name', label: 'Supplier', type: 'text' },
      { key: 'order_number', label: 'PO Number', type: 'text' },
      { key: 'date_from', label: 'From', type: 'date' },
      { key: 'date_to', label: 'To', type: 'date' },
    ],
  },
  {
    id: 'by-supplier', label: 'Receipt by Supplier', icon: Building2,
    desc: 'GRN counts grouped by supplier.',
    color: 'bg-blue-50', iconColor: 'text-blue-600',
  },
  {
    id: 'outstanding-deliveries', label: 'Outstanding Deliveries', icon: Truck,
    desc: 'Purchase orders awaiting full delivery.',
    color: 'bg-amber-50', iconColor: 'text-amber-600',
  },
  {
    id: 'delivery-performance', label: 'Delivery Performance', icon: ClipboardList,
    desc: 'On-time vs late delivery analysis for all GRNs.',
    color: 'bg-emerald-50', iconColor: 'text-emerald-600', badge: 'Analysis', badgeColor: 'bg-emerald-100 text-emerald-700',
  },
];

// ═══════════════════════ PO REPORTS ═══════════════════════

export const PO_REPORTS = [
  {
    id: 'management', label: 'General Management Report', icon: SlidersHorizontal, ...MGMT, advancedModule: 'orders',
    desc: 'Query any PO attribute with full boolean logic. Save and recall filters.',
    drillDown: { keyField: 'order_number', route: '/orders' },
    reportFormats: [
      {
        value: 'header_only',
        label: 'Header Details Only',
        type: 'flat',
        linesField: 'line_items',
        columns: [
          { key: 'order_number', label: 'Order Number', mono: true },
          { key: 'status', label: 'Order Status' },
          { key: 'order_date', label: 'Order Date' },
          { key: 'document_type_name', label: 'Document Type' },
          { key: 'ref_num', label: 'Ref Num' },
          { key: 'supplier_name', label: 'Supplier Name' },
          { key: 'delivery_point_code', label: 'Delivery Code' },
          { key: '_line_count', label: '# Line Items', align: 'right' },
          { key: '_doc_value', label: 'Reported Order Value', align: 'right', currency: true },
        ],
        valueFields: ['order_total', 'net_total'],
        totalField: '_doc_value',
        totalLabel: 'Total Order Value',
        excludeStatuses: ['CANCELLED'],
        noteText: 'Any CANCELLED order listed will not be part of the total order value.',
      },
      {
        value: 'line_only',
        label: 'Line Details Only',
        type: 'flat',
        linesField: 'line_items',
        parentFields: ['order_number', 'status'],
        columns: [
          { key: 'order_number', label: 'Order Number', mono: true },
          { key: 'status', label: 'Order Status' },
          { key: 'item_due_date', label: 'Due Date' },
          { key: 'item_num', label: 'Item No', align: 'right' },
          { key: 'item_description', label: 'Description' },
          { key: 'item_code', label: 'Item Code' },
          { key: 'unit', label: 'Unit' },
          { key: 'quantity', label: 'Quantity', align: 'right', qty: true },
          { key: 'discount_pct', label: 'Discount', align: 'right', pct: true },
          { key: 'unit_price', label: 'Reported Price', align: 'right', currency: true },
          { key: 'net_price', label: 'Reported Net Price', align: 'right', currency: true },
        ],
        totalField: 'net_price',
        totalLabel: 'Report Total',
        excludeStatuses: ['CANCELLED'],
        noteText: 'Any CANCELLED order listed will not be part of the report total.',
      },
      {
        value: 'combo',
        label: 'Header & Line Details',
        type: 'combo',
        linesField: 'line_items',
        primaryKeyField: 'order_number',
        primaryKeyLabel: 'Order Number',
        valueFields: ['order_total', 'net_total'],
        headerSections: [
          [
            { key: 'status', label: 'Status' },
            { key: 'order_date', label: 'Order Date' },
            { key: 'document_type_name', label: 'Order Type' },
            { key: 'issued_date', label: 'Issue Date' },
          ],
          [
            { key: 'ref_num', label: 'Ref Num' },
            { key: 'buyer_name', label: 'Order Originator' },
            { key: 'buyer_name', label: 'Buyer' },
            { key: 'supplier_name', label: 'Supplier' },
          ],
          [
            { key: 'category_code', label: 'Category Code' },
            { key: 'account', label: 'Account' },
            { key: 'restaurant', label: 'Restaurant' },
            { key: 'department_code', label: 'Department Code' },
          ],
          [
            { key: 'delivery_point_code', label: 'Delivery Point' },
            { key: 'invoice_point_name', label: 'Invoice Point' },
            { key: 'contract_number', label: 'Contract Number' },
          ],
        ],
        lineColumns: [
          { key: 'item_num', label: 'Item', align: 'right' },
          { key: 'item_description', label: 'Description' },
          { key: 'quantity', label: 'Quantity', align: 'right' },
          { key: 'discount_pct', label: 'Discount', align: 'right' },
          { key: 'unit_price', label: 'Unit Price', align: 'right' },
          { key: 'net_price', label: 'Line Price', align: 'right' },
          { key: 'item_due_date', label: 'Due Date' },
          { key: 'account_code', label: 'Opex Caex GL' },
          { key: 'category_code', label: 'Property Code' },
          { key: 'account_code_3', label: 'Nominal Code' },
        ],
      },
    ],
  },
  {
    id: 'register', label: 'PO Register', icon: FileText,
    desc: 'Complete purchase order listing with status and values.',
    color: 'bg-indigo-50', iconColor: 'text-indigo-600', badge: 'Core',
    drillDown: { keyField: 'order_number', route: '/orders' },
    lineItemColumns: [
      { key: 'li_item_code', label: 'Item Code', mono: true },
      { key: 'li_item_description', label: 'Description' },
      { key: 'li_quantity', label: 'Qty', align: 'right' },
      { key: 'li_unit_price', label: 'Unit Price', align: 'right' },
      { key: 'li_net_price', label: 'Net Price', align: 'right' },
      { key: 'li_account_code', label: 'Account Code' },
      { key: 'li_category_code', label: 'Category' },
    ],
    filters: [
      { key: 'order_number', label: 'Order Number', type: 'text', placeholder: 'e.g. PO-00001' },
      { key: 'status', label: 'Status', type: 'select', options: [
        { value: 'DRAFT', label: 'Draft' }, { value: 'ISSUED', label: 'Issued' },
        { value: 'PARTIALLY_RECEIVED', label: 'Partially Received' }, { value: 'FULLY_RECEIVED', label: 'Fully Received' },
        { value: 'CANCELLED', label: 'Cancelled' },
      ]},
      { key: 'supplier_name', label: 'Supplier', type: 'text' },
      { key: 'created_by', label: 'Created By', type: 'text' },
      { key: 'date_from', label: 'From', type: 'date' },
      { key: 'date_to', label: 'To', type: 'date' },
    ],
  },
  {
    id: 'spend-by-supplier', label: 'Spend by Supplier', icon: Building2,
    desc: 'Purchase order spend aggregated by supplier.',
    color: 'bg-blue-50', iconColor: 'text-blue-600', badge: 'Analysis', badgeColor: 'bg-purple-100 text-purple-700',
    filters: [
      { key: 'date_from', label: 'From Date', type: 'date' },
      { key: 'date_to', label: 'To Date', type: 'date' },
    ],
  },
  {
    id: 'spend-by-category', label: 'Spend by Category', icon: BarChart3,
    desc: 'Line-item spend broken down by category code.',
    color: 'bg-purple-50', iconColor: 'text-purple-600', badge: 'Analysis', badgeColor: 'bg-purple-100 text-purple-700',
    filters: [
      { key: 'date_from', label: 'From Date', type: 'date' },
      { key: 'date_to', label: 'To Date', type: 'date' },
    ],
  },
  {
    id: 'outstanding', label: 'Outstanding POs', icon: Clock,
    desc: 'Orders issued or partially received, still open.',
    color: 'bg-amber-50', iconColor: 'text-amber-600',
    drillDown: { keyField: 'order_number', route: '/orders' },
    filters: [
      { key: 'order_number', label: 'Order Number', type: 'text' },
      { key: 'supplier_name', label: 'Supplier', type: 'text' },
      { key: 'date_from', label: 'From', type: 'date' },
      { key: 'date_to', label: 'To', type: 'date' },
    ],
  },
];

// ═══════════════════════ RFX REPORTS ═══════════════════════

export const RFX_REPORTS = [
  {
    id: 'management', label: 'General Management Report', icon: SlidersHorizontal, ...MGMT, advancedModule: 'rfx',
    desc: 'Query any RFx attribute with full boolean logic. Save and recall filters.',
    drillDown: { keyField: 'rfx_number', route: '/sourcing-events', param: 'edit' },
  },
  {
    id: 'activity', label: 'RFx Activity Report', icon: FileEdit,
    desc: 'All RFx events (RFI, RFP, RFQ, Auction) with status and participation.',
    color: 'bg-indigo-50', iconColor: 'text-indigo-600', badge: 'Core',
    drillDown: { keyField: 'rfx_number', route: '/sourcing-events', param: 'edit' },
    filters: [
      { key: 'rfx_number', label: 'RFx #', type: 'text', placeholder: 'e.g. RFX-001' },
      { key: 'rfx_type', label: 'Type', type: 'select', options: [
        { value: 'RFI', label: 'RFI' }, { value: 'RFP', label: 'RFP' },
        { value: 'RFQ', label: 'RFQ' }, { value: 'REVERSE_AUCTION', label: 'Auction' },
      ]},
      { key: 'status', label: 'Status', type: 'text' },
      { key: 'title', label: 'Title', type: 'text' },
      { key: 'category', label: 'Category', type: 'text' },
      { key: 'created_by_name', label: 'Created By', type: 'text' },
    ],
    columns: [
      { key: 'rfx_number', label: 'RFx #', mono: true },
      { key: 'rfx_type', label: 'Type' },
      { key: 'title', label: 'Title' },
      { key: 'status', label: 'Status' },
      { key: 'category', label: 'Category' },
      { key: 'created_at', label: 'Created' },
      { key: 'created_by_name', label: 'Created By' },
      { key: 'supplier_count', label: 'Suppliers', align: 'right' },
    ],
  },
  {
    id: 'award-summary', label: 'Award Summary', icon: Award,
    desc: 'Awarded RFx events with supplier and value details.',
    color: 'bg-emerald-50', iconColor: 'text-emerald-600',
  },
  {
    id: 'savings', label: 'Sourcing Savings', icon: PiggyBank,
    desc: 'Savings recorded from sourcing activities.',
    color: 'bg-amber-50', iconColor: 'text-amber-600', badge: 'Finance', badgeColor: 'bg-amber-100 text-amber-700',
  },
];

// ═══════════════════════ REQUISITION REPORTS ═══════════════════════

export const REQUISITION_REPORTS = [
  {
    id: 'management', label: 'General Management Report', icon: SlidersHorizontal, ...MGMT, advancedModule: 'requisitions',
    desc: 'Query any requisition attribute with full boolean logic. Save and recall filters.',
    drillDown: { keyField: 'request_number', route: '/requests' },
    reportFormats: [
      {
        value: 'header_only',
        label: 'Header Details Only',
        type: 'flat',
        linesField: 'line_items',
        columns: [
          { key: 'request_number', label: 'Request Number', mono: true },
          { key: 'status', label: 'Status' },
          { key: 'entry_date', label: 'Entry Date' },
          { key: 'document_type', label: 'Document Type' },
          { key: 'ref_num', label: 'Ref Num' },
          { key: 'originator_name', label: 'Originator' },
          { key: 'delivery_code', label: 'Delivery Code' },
          { key: '_line_count', label: '# Line Items', align: 'right' },
          { key: '_doc_value', label: 'Reported Req Value', align: 'right', currency: true },
        ],
        valueFields: ['request_net_total'],
        totalField: '_doc_value',
        totalLabel: 'Total Requisition Value',
        excludeStatuses: ['REJECTED'],
        noteText: 'Any REJECTED requisition listed will not be part of the total requisition value.',
      },
      {
        value: 'line_only',
        label: 'Line Details Only',
        type: 'flat',
        linesField: 'line_items',
        parentFields: ['request_number', 'status'],
        columns: [
          { key: 'request_number', label: 'Request Number', mono: true },
          { key: 'status', label: 'Status' },
          { key: 'item_due_date', label: 'Due Date' },
          { key: 'item_num', label: 'Item No', align: 'right' },
          { key: 'item_description', label: 'Description' },
          { key: 'item_code', label: 'Item Code' },
          { key: 'unit', label: 'Unit' },
          { key: 'quantity', label: 'Quantity', align: 'right', qty: true },
          { key: 'discount_pct', label: 'Discount', align: 'right', pct: true },
          { key: 'unit_price', label: 'Reported Price', align: 'right', currency: true },
          { key: 'net_price', label: 'Reported Net Price', align: 'right', currency: true },
        ],
        totalField: 'net_price',
        totalLabel: 'Report Total',
        excludeStatuses: ['REJECTED'],
        noteText: 'Any REJECTED requisition listed will not be part of the report total.',
      },
      {
        value: 'combo',
        label: 'Header & Line Details',
        type: 'combo',
        linesField: 'line_items',
        primaryKeyField: 'request_number',
        primaryKeyLabel: 'Request Number',
        valueFields: ['request_net_total'],
        headerSections: [
          [
            { key: 'status', label: 'Status' },
            { key: 'entry_date', label: 'Entry Date' },
            { key: 'document_type', label: 'Document Type' },
            { key: 'ref_num', label: 'Ref Num' },
          ],
          [
            { key: 'originator_name', label: 'Originator' },
            { key: 'delivery_to', label: 'Delivery To' },
            { key: 'delivery_code', label: 'Delivery Code' },
            { key: 'currency', label: 'Currency' },
          ],
          [
            { key: 'category_code', label: 'Category Code' },
            { key: 'account', label: 'Account' },
            { key: 'department_code', label: 'Department Code' },
            { key: 'restaurant', label: 'Restaurant' },
          ],
          [
            { key: 'budget_status', label: 'Budget Status' },
            { key: 'shipping_method', label: 'Shipping Method' },
            { key: 'incoterms', label: 'Incoterms' },
          ],
        ],
        lineColumns: [
          { key: 'item_num', label: 'Item', align: 'right' },
          { key: 'item_description', label: 'Description' },
          { key: 'quantity', label: 'Quantity', align: 'right', qty: true },
          { key: 'discount_pct', label: 'Discount', align: 'right', pct: true },
          { key: 'unit_price', label: 'Unit Price', align: 'right', currency: true },
          { key: 'net_price', label: 'Line Price', align: 'right', currency: true },
          { key: 'item_due_date', label: 'Due Date' },
          { key: 'supplier_code', label: 'Supplier Code' },
        ],
      },
    ],
  },
  {
    id: 'register', label: 'Requisition Register', icon: ClipboardList,
    desc: 'All requisitions with status, requester, and value.',
    color: 'bg-indigo-50', iconColor: 'text-indigo-600', badge: 'Core',
    drillDown: { keyField: 'request_number', route: '/requests' },
    lineItemColumns: [
      { key: 'li_item_code', label: 'Item Code', mono: true },
      { key: 'li_item_description', label: 'Description' },
      { key: 'li_quantity', label: 'Qty', align: 'right' },
      { key: 'li_unit_price', label: 'Unit Price', align: 'right' },
      { key: 'li_net_price', label: 'Net Price', align: 'right' },
    ],
    filters: [
      { key: 'request_number', label: 'Request Number', type: 'text', placeholder: 'e.g. REQ-001' },
      { key: 'status', label: 'Status', type: 'select', options: [
        { value: 'PENDING', label: 'Pending' }, { value: 'AWAITING_APPROVAL', label: 'Awaiting Approval' },
        { value: 'APPROVED', label: 'Approved' }, { value: 'ORDERED', label: 'Ordered' },
        { value: 'REJECTED', label: 'Rejected' },
      ]},
      { key: 'requester', label: 'Requester', type: 'text' },
      { key: 'created_by', label: 'Created By', type: 'text' },
      { key: 'date_from', label: 'From', type: 'date' },
      { key: 'date_to', label: 'To', type: 'date' },
    ],
  },
  {
    id: 'aging', label: 'Requisition Aging', icon: Clock,
    desc: 'Pending requisitions aged by days waiting.',
    color: 'bg-amber-50', iconColor: 'text-amber-600',
  },
  {
    id: 'approval-turnaround', label: 'Approval Turnaround', icon: Calendar,
    desc: 'Time from submission to approval for completed requisitions.',
    color: 'bg-emerald-50', iconColor: 'text-emerald-600', badge: 'Analysis', badgeColor: 'bg-emerald-100 text-emerald-700',
  },
  {
    id: 'spend-by-requester', label: 'Spend by Requester', icon: Users,
    desc: 'Requisition value aggregated by requester.',
    color: 'bg-blue-50', iconColor: 'text-blue-600',
  },
];

// ═══════════════════════ CONTRACT REPORTS ═══════════════════════

export const CONTRACT_REPORTS = [
  {
    id: 'management', label: 'General Management Report', icon: SlidersHorizontal, ...MGMT, advancedModule: 'contracts',
    desc: 'Query any contract attribute with full boolean logic. Save and recall filters.',
    drillDown: { keyField: 'contract_number', route: '/data/contracts', param: 'edit' },
  },
  {
    id: 'register', label: 'Contract Register', icon: Briefcase,
    desc: 'All contracts with supplier, dates, and values.',
    color: 'bg-indigo-50', iconColor: 'text-indigo-600', badge: 'Core',
    drillDown: { keyField: 'contract_number', route: '/data/contracts', param: 'edit' },
    filters: [
      { key: 'contract_number', label: 'Contract Number', type: 'text', placeholder: 'e.g. CON-001' },
      { key: 'status', label: 'Status', type: 'text' },
      { key: 'supplier_name', label: 'Supplier', type: 'text' },
      { key: 'date_from', label: 'From', type: 'date' },
      { key: 'date_to', label: 'To', type: 'date' },
    ],
  },
  {
    id: 'expiring', label: 'Expiring Contracts', icon: Calendar,
    desc: 'Contracts expiring within the next 90 days.',
    color: 'bg-red-50', iconColor: 'text-red-600', badge: 'Alert', badgeColor: 'bg-red-100 text-red-700',
  },
  {
    id: 'by-supplier', label: 'Value by Supplier', icon: Building2,
    desc: 'Contract counts and values grouped by supplier.',
    color: 'bg-blue-50', iconColor: 'text-blue-600',
  },
];

// ═══════════════════════ ACCOUNT CODE REPORTS ═══════════════════════

export const ACCOUNT_CODE_REPORTS = [
  {
    id: 'management', label: 'General Management Report', icon: SlidersHorizontal, ...MGMT, advancedModule: 'account-codes',
    desc: 'Query any account code attribute with full boolean logic. Save and recall filters.',
  },
  {
    id: 'usage', label: 'Account Code Listing', icon: Hash,
    desc: 'Complete listing of all configured account codes with budget and status.',
    color: 'bg-indigo-50', iconColor: 'text-indigo-600', badge: 'Core',
    filters: [
      { key: 'code', label: 'Code', type: 'text', placeholder: 'e.g. 1001' },
      { key: 'description', label: 'Description', type: 'text' },
      { key: 'segment_number', label: 'Segment', type: 'select', options: [
        { value: '1', label: 'Segment 1' }, { value: '2', label: 'Segment 2' },
        { value: '3', label: 'Segment 3' }, { value: '4', label: 'Segment 4' },
        { value: '5', label: 'Segment 5' }, { value: '6', label: 'Segment 6' },
        { value: '7', label: 'Segment 7' },
      ]},
      { key: 'active', label: 'Active', type: 'select', options: [
        { value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' },
      ]},
    ],
    columns: [
      { key: 'code', label: 'Code', mono: true },
      { key: 'description', label: 'Description' },
      { key: 'segment_label', label: 'Segment' },
      { key: 'budget', label: 'Budget', align: 'right' },
      { key: 'active', label: 'Active' },
    ],
  },
  {
    id: 'segment-analysis', label: 'Segment Analysis', icon: Grid3X3,
    desc: 'Account code segment structure, configuration, and code counts.',
    color: 'bg-purple-50', iconColor: 'text-purple-600',
    columns: [
      { key: 'segment_number', label: 'Segment #', align: 'right' },
      { key: 'label', label: 'Label' },
      { key: 'active', label: 'Active' },
      { key: 'required', label: 'Required' },
      { key: 'code_count', label: 'Codes', align: 'right' },
    ],
  },
  {
    id: 'budget-summary', label: 'Budget Summary', icon: DollarSign,
    desc: 'Total budget allocation by segment with active/inactive code counts.',
    color: 'bg-emerald-50', iconColor: 'text-emerald-600', badge: 'Finance', badgeColor: 'bg-emerald-100 text-emerald-700',
    columns: [
      { key: 'segment_label', label: 'Segment' },
      { key: 'code_count', label: 'Codes', align: 'right' },
      { key: 'active_count', label: 'Active', align: 'right' },
      { key: 'inactive_count', label: 'Inactive', align: 'right' },
      { key: 'total_budget', label: 'Total Budget', align: 'right' },
    ],
  },
  {
    id: 'inactive-codes', label: 'Inactive Codes', icon: AlertTriangle,
    desc: 'All inactive account codes that may need review or cleanup.',
    color: 'bg-red-50', iconColor: 'text-red-600', badge: 'Alert', badgeColor: 'bg-red-100 text-red-700',
    columns: [
      { key: 'code', label: 'Code', mono: true },
      { key: 'description', label: 'Description' },
      { key: 'segment_label', label: 'Segment' },
      { key: 'budget', label: 'Budget', align: 'right' },
    ],
  },
];

// ═══════════════════════ DATA FILES REPORTS ═══════════════════════

export const DATA_FILES_REPORTS = [
  {
    id: 'management-items', label: 'Items Management Report', icon: SlidersHorizontal, ...MGMT, advancedModule: 'data-items',
    desc: 'Query any item attribute with full boolean logic. Save and recall filters.',
  },
  {
    id: 'management-suppliers', label: 'Suppliers Management Report', icon: SlidersHorizontal, ...MGMT, advancedModule: 'data-suppliers',
    desc: 'Query any supplier attribute with full boolean logic. Save and recall filters.',
  },
  {
    id: 'management-categories', label: 'Categories Management Report', icon: SlidersHorizontal, ...MGMT, advancedModule: 'data-categories',
    desc: 'Query any category attribute with full boolean logic. Save and recall filters.',
  },
  {
    id: 'items', label: 'Items Listing', icon: Database,
    desc: 'Master data listing of all items with type, category, and pricing.',
    color: 'bg-indigo-50', iconColor: 'text-indigo-600', badge: 'Master Data',
    drillDown: { keyField: 'item_code', route: '/data/items', param: 'edit' },
    filters: [
      { key: 'item_code', label: 'Item Code', type: 'text', placeholder: 'e.g. ITEM-001' },
      { key: 'description', label: 'Description', type: 'text' },
      { key: 'item_type', label: 'Type', type: 'select', options: [
        { value: 'Consumable', label: 'Consumable' }, { value: 'Fixed Asset', label: 'Fixed Asset' },
        { value: 'Current Asset', label: 'Current Asset' },
      ]},
      { key: 'active', label: 'Active', type: 'select', options: [
        { value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' },
      ]},
      { key: 'category_code', label: 'Category', type: 'text' },
      { key: 'supplier_name', label: 'Supplier Name', type: 'text' },
      { key: 'uom', label: 'UOM', type: 'text' },
    ],
    columns: [
      { key: 'item_code', label: 'Item Code', mono: true },
      { key: 'description', label: 'Description' },
      { key: 'item_type', label: 'Item Type' },
      { key: 'category_code', label: 'Category Code' },
      { key: 'active', label: 'Active' },
      { key: 'supplier_name', label: 'Supplier Name' },
      { key: 'unit_price', label: 'Unit Price', align: 'right' },
      { key: 'uom', label: 'UOM' },
    ],
  },
  {
    id: 'suppliers', label: 'Suppliers Listing', icon: Tag,
    desc: 'Master data listing of all registered suppliers with contact details.',
    color: 'bg-blue-50', iconColor: 'text-blue-600', badge: 'Master Data',
    drillDown: { keyField: 'supplier_code', route: '/data/suppliers', param: 'edit' },
    filters: [
      { key: 'supplier_code', label: 'Supplier Code', type: 'text', placeholder: 'e.g. SUP-001' },
      { key: 'supplier_name', label: 'Supplier Name', type: 'text', placeholder: 'e.g. Acme' },
      { key: 'status', label: 'Status', type: 'text', placeholder: 'e.g. Approved' },
      { key: 'category', label: 'Category', type: 'text', placeholder: 'e.g. Strategic' },
      { key: 'country', label: 'Country', type: 'text' },
      { key: 'city', label: 'City', type: 'text' },
      { key: 'active', label: 'Active', type: 'select', options: [
        { value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' },
      ]},
    ],
    columns: [
      { key: 'supplier_code', label: 'Supplier Code', mono: true },
      { key: 'supplier_name', label: 'Supplier Name' },
      { key: 'contact_email', label: 'Contact Email' },
      { key: 'status', label: 'Status' },
      { key: 'category', label: 'Category' },
      { key: 'address', label: 'Address', addressCol: true },
      { key: 'city', label: 'City', addressCol: true },
      { key: 'state_province', label: 'State / Province', addressCol: true },
      { key: 'zip_postal', label: 'Zip / Postal', addressCol: true },
      { key: 'country', label: 'Country' },
      { key: 'active', label: 'Active' },
    ],
  },
  {
    id: 'categories', label: 'Categories Listing', icon: FolderTree,
    desc: 'Master data listing of all category codes with budgets.',
    color: 'bg-emerald-50', iconColor: 'text-emerald-600', badge: 'Master Data',
    filters: [
      { key: 'code', label: 'Code', type: 'text', placeholder: 'e.g. CAT-001' },
      { key: 'description', label: 'Description', type: 'text' },
      { key: 'parent_code', label: 'Parent Code', type: 'text' },
      { key: 'active', label: 'Active', type: 'select', options: [
        { value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' },
      ]},
    ],
    columns: [
      { key: 'code', label: 'Code', mono: true },
      { key: 'description', label: 'Description' },
      { key: 'active', label: 'Active' },
      { key: 'budget', label: 'Budget', align: 'right' },
      { key: 'parent_code', label: 'Parent Code' },
    ],
  },
  {
    id: 'currencies', label: 'Currencies Listing', icon: DollarSign,
    desc: 'All configured currency codes and exchange rates.',
    color: 'bg-amber-50', iconColor: 'text-amber-600', badge: 'Master Data',
    filters: [
      { key: 'code', label: 'Code', type: 'text', placeholder: 'e.g. USD' },
      { key: 'description', label: 'Description', type: 'text' },
      { key: 'active', label: 'Active', type: 'select', options: [
        { value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' },
      ]},
    ],
    columns: [
      { key: 'code', label: 'Code', mono: true },
      { key: 'description', label: 'Description' },
      { key: 'exchange_rate', label: 'Exchange Rate', align: 'right' },
      { key: 'is_base', label: 'Base Currency' },
      { key: 'active', label: 'Active' },
      { key: 'last_updated', label: 'Last Updated', tzLabel: true },
    ],
  },
  {
    id: 'delivery-points', label: 'Delivery Points Listing', icon: Truck,
    desc: 'All delivery point codes, addresses, and contacts.',
    color: 'bg-teal-50', iconColor: 'text-teal-600', badge: 'Master Data',
    filters: [
      { key: 'code', label: 'Code', type: 'text', placeholder: 'e.g. DP-001' },
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'city', label: 'City', type: 'text' },
      { key: 'country', label: 'Country', type: 'text' },
      { key: 'contact_name', label: 'Contact', type: 'text' },
      { key: 'active', label: 'Active', type: 'select', options: [
        { value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' },
      ]},
    ],
    columns: [
      { key: 'code', label: 'Code', mono: true },
      { key: 'name', label: 'Name' },
      { key: 'address', label: 'Address', addressCol: true },
      { key: 'city', label: 'City', addressCol: true },
      { key: 'state_province', label: 'State / Province', addressCol: true },
      { key: 'zip_postal', label: 'Zip / Postal', addressCol: true },
      { key: 'country', label: 'Country' },
      { key: 'contact_name', label: 'Contact' },
      { key: 'telephone', label: 'Telephone' },
      { key: 'email', label: 'Email' },
      { key: 'active', label: 'Active' },
    ],
  },
  {
    id: 'document-types', label: 'Document Types Listing', icon: FileText,
    desc: 'All configured document type codes.',
    color: 'bg-sky-50', iconColor: 'text-sky-600', badge: 'Master Data',
    filters: [
      { key: 'code', label: 'Code', type: 'text', placeholder: 'e.g. DOC-001' },
      { key: 'description', label: 'Description', type: 'text' },
      { key: 'company_name', label: 'Company Name', type: 'text' },
      { key: 'active', label: 'Active', type: 'select', options: [
        { value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' },
      ]},
    ],
    columns: [
      { key: 'code', label: 'Code', mono: true },
      { key: 'description', label: 'Description' },
      { key: 'company_name', label: 'Company Name' },
      { key: 'active', label: 'Active' },
    ],
  },
  {
    id: 'invoice-points', label: 'Invoice Points Listing', icon: Receipt,
    desc: 'All invoice point codes, addresses, and contacts.',
    color: 'bg-violet-50', iconColor: 'text-violet-600', badge: 'Master Data',
    filters: [
      { key: 'code', label: 'Code', type: 'text', placeholder: 'e.g. IP-001' },
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'city', label: 'City', type: 'text' },
      { key: 'country', label: 'Country', type: 'text' },
      { key: 'contact_name', label: 'Contact', type: 'text' },
      { key: 'email', label: 'Email', type: 'text' },
      { key: 'active', label: 'Active', type: 'select', options: [
        { value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' },
      ]},
    ],
    columns: [
      { key: 'code', label: 'Code', mono: true },
      { key: 'name', label: 'Name' },
      { key: 'address', label: 'Address', addressCol: true },
      { key: 'city', label: 'City', addressCol: true },
      { key: 'state_province', label: 'State / Province', addressCol: true },
      { key: 'zip_postal', label: 'Zip / Postal', addressCol: true },
      { key: 'country', label: 'Country' },
      { key: 'contact_name', label: 'Contact' },
      { key: 'telephone', label: 'Telephone' },
      { key: 'email', label: 'Email' },
      { key: 'active', label: 'Active' },
    ],
  },
  {
    id: 'terms', label: 'Terms & Conditions Listing', icon: FileEdit,
    desc: 'All terms and conditions templates with scope details.',
    color: 'bg-rose-50', iconColor: 'text-rose-600', badge: 'Master Data',
    filters: [
      { key: 'code', label: 'Code', type: 'text', placeholder: 'e.g. TC-001' },
      { key: 'outline', label: 'Outline', type: 'text' },
      { key: 'file_type', label: 'File Type', type: 'text' },
      { key: 'active', label: 'Active', type: 'select', options: [
        { value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' },
      ]},
    ],
    columns: [
      { key: 'code', label: 'Code', mono: true },
      { key: 'outline', label: 'Outline' },
      { key: 'file_type', label: 'File Type' },
      { key: 'order_default', label: 'PO Default' },
      { key: 'request_default', label: 'Req Default' },
      { key: 'rfq_default', label: 'RFQ Default' },
      { key: 'active', label: 'Active' },
    ],
  },
  {
    id: 'group-items', label: 'Group Items Listing', icon: Layers,
    desc: 'All group item configurations and member counts.',
    color: 'bg-cyan-50', iconColor: 'text-cyan-600', badge: 'Master Data',
    filters: [
      { key: 'name', label: 'Group Name', type: 'text' },
      { key: 'description', label: 'Description', type: 'text' },
      { key: 'active', label: 'Active', type: 'select', options: [
        { value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' },
      ]},
    ],
    columns: [
      { key: 'name', label: 'Group Name' },
      { key: 'description', label: 'Description' },
      { key: 'active', label: 'Active' },
      { key: 'item_count', label: 'Item Count', align: 'right' },
      { key: 'created_by', label: 'Created By' },
    ],
  },
  {
    id: 'customers', label: 'Customers Listing', icon: Users,
    desc: 'Master data listing of all customer records with contact details.',
    color: 'bg-orange-50', iconColor: 'text-orange-600', badge: 'Master Data',
    filters: [
      { key: 'customer_code', label: 'Customer Code', type: 'text', placeholder: 'e.g. CUST-001' },
      { key: 'customer_name', label: 'Customer Name', type: 'text' },
      { key: 'customer_type', label: 'Type', type: 'text' },
      { key: 'city', label: 'City', type: 'text' },
      { key: 'country', label: 'Country', type: 'text' },
      { key: 'currency', label: 'Currency', type: 'text' },
      { key: 'payment_terms', label: 'Payment Terms', type: 'text' },
      { key: 'active', label: 'Active', type: 'select', options: [
        { value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' },
      ]},
    ],
    columns: [
      { key: 'customer_code', label: 'Customer Code', mono: true },
      { key: 'customer_name', label: 'Customer Name' },
      { key: 'customer_type', label: 'Type' },
      { key: 'city', label: 'City' },
      { key: 'country', label: 'Country' },
      { key: 'active', label: 'Active' },
      { key: 'credit_limit', label: 'Credit Limit', align: 'right' },
      { key: 'currency', label: 'Currency' },
      { key: 'payment_terms', label: 'Payment Terms' },
    ],
  },
];


// ═══════════════════════ PER-SECTION DATA FILES REPORTS ═══════════════════════
export const ITEMS_REPORTS = DATA_FILES_REPORTS.filter(r => ['management-items', 'items'].includes(r.id));
export const SUPPLIERS_REPORTS = DATA_FILES_REPORTS.filter(r => ['management-suppliers', 'suppliers'].includes(r.id));
export const CATEGORIES_REPORTS = DATA_FILES_REPORTS.filter(r => ['management-categories', 'categories'].includes(r.id));
export const CURRENCIES_REPORTS = DATA_FILES_REPORTS.filter(r => r.id === 'currencies');
export const DELIVERY_POINTS_REPORTS = DATA_FILES_REPORTS.filter(r => r.id === 'delivery-points');
export const DOCUMENT_TYPES_REPORTS = DATA_FILES_REPORTS.filter(r => r.id === 'document-types');
export const INVOICE_POINTS_REPORTS = DATA_FILES_REPORTS.filter(r => r.id === 'invoice-points');
export const TERMS_REPORTS = DATA_FILES_REPORTS.filter(r => r.id === 'terms');
export const GROUP_ITEMS_REPORTS = DATA_FILES_REPORTS.filter(r => r.id === 'group-items');
export const CUSTOMERS_REPORTS = DATA_FILES_REPORTS.filter(r => r.id === 'customers');
